"use client";

import { useAuth } from "@clerk/nextjs";
import { GraduationCap } from "lucide-react";
import Link from "next/link";
import { startTransition, useActionState, useEffect, useState } from "react";
import { Button } from "~/components/ui/button";
import { ToastAction } from "~/components/ui/toast";
import { toast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import {
  type ToggleIsStudyingActionState,
  toggleIsStudyingAction,
} from "~/server/actions/videos/actions";

export default function ToggleIsStudying({
  videoId,
  defaultIsStudying,
  isImportCompleted = false,
  className,
}: {
  videoId: number;
  defaultIsStudying: boolean;
  isImportCompleted?: boolean;
  className?: string;
}) {
  const [state, formAction] = useActionState<
    ToggleIsStudyingActionState,
    FormData
  >(toggleIsStudyingAction, { message: "" });

  const { isSignedIn } = useAuth();

  const [isStudying, setIsStudying] = useState(defaultIsStudying);

  useEffect(() => {
    if (!state.success) {
      setIsStudying(state.isStudying ?? defaultIsStudying);
      return;
    }
    toast({
      title: state.isStudying
        ? `Added ${state.totalCount} words to your study list`
        : `Removed ${state.totalCount} words from your study list`,
      description: state.isStudying
        ? "You can review or study them in the study section."
        : "These words will no longer appear in your study list.",
      action: state.isStudying ? (
        <ToastAction altText="Go to review" asChild>
          <Link href="/words/review">Go Review</Link>
        </ToastAction>
      ) : undefined,
    });
  }, [state, defaultIsStudying]);

  const handleToggleFavorite = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsStudying(!isStudying);
    startTransition(() => {
      formAction(new FormData(event.currentTarget));
    });
  };

  return (
    <form onSubmit={handleToggleFavorite} className={cn("contents", className)}>
      <input type="hidden" name="videoId" value={videoId} />
      <Button
        type="submit"
        size="icon"
        variant="ghost"
        disabled={!isImportCompleted || !isSignedIn || false}
        aria-label={isStudying ? "Remove from studying" : "Add to studying"}
      >
        <GraduationCap
          className={cn("h-4 w-4", { "fill-current": isStudying })}
        />
      </Button>
    </form>
  );
}
